<div class="space-y-4 sm:space-y-6">
  <!-- ── MANAGER VIEW ──────────────────────────────────────────────────────── -->
  @if (isManagerView()) {
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ 'ATTENDANCE.TITLE' | translate }}
        </h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {{ 'ATTENDANCE.SUBTITLE' | translate }}
        </p>
      </div>
      <a
        href="/check-in"
        target="_blank"
        class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors self-start sm:self-auto"
      >
        <lucide-icon [img]="Download" [size]="16" />
        {{ 'ATTENDANCE.EMPLOYEE_PAGE' | translate }}
      </a>
    </div>

    <!-- Today quick stats (today tab only) -->
    @if (managerTab() === 'today') {
      <div class="grid grid-cols-3 gap-3">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-100 dark:border-gray-700"
        >
          <div class="flex items-center gap-2 mb-1">
            <lucide-icon [img]="Users" class="text-gray-400" [size]="18" />
            <span class="text-xs text-gray-500 dark:text-gray-400">{{
              'ATTENDANCE.STATS.TOTAL' | translate
            }}</span>
          </div>
          <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ todayList().length }}</p>
        </div>
        <div
          class="bg-green-50 dark:bg-green-900/20 rounded-xl shadow-sm p-4 border border-green-100 dark:border-green-800"
        >
          <div class="flex items-center gap-2 mb-1">
            <lucide-icon [img]="UserCheck" class="text-green-500" [size]="18" />
            <span class="text-xs text-green-600 dark:text-green-400">{{
              'ATTENDANCE.STATS.PRESENT' | translate
            }}</span>
          </div>
          <p class="text-2xl font-bold text-green-700 dark:text-green-400">{{ presentCount }}</p>
        </div>
        <div
          class="bg-red-50 dark:bg-red-900/20 rounded-xl shadow-sm p-4 border border-red-100 dark:border-red-800"
        >
          <div class="flex items-center gap-2 mb-1">
            <lucide-icon [img]="UserX" class="text-red-400" [size]="18" />
            <span class="text-xs text-red-600 dark:text-red-400">{{
              'ATTENDANCE.STATS.ABSENT' | translate
            }}</span>
          </div>
          <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ absentCount }}</p>
        </div>
      </div>
    }

    <!-- Summary quick stats -->
    @if (managerTab() === 'summary' && !summaryLoading() && summaryList().length > 0) {
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700"
        >
          <p class="text-xs text-gray-500 dark:text-gray-400">
            {{ 'ATTENDANCE.SUMMARY.EMPLOYEES' | translate }}
          </p>
          <p class="text-xl font-bold text-gray-800 dark:text-white mt-1">
            {{ summaryStats().totalEmployees }}
          </p>
        </div>
        <div
          class="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-800"
        >
          <p class="text-xs text-green-600 dark:text-green-400">
            {{ 'ATTENDANCE.SUMMARY.AVG_PRESENCE' | translate }}
          </p>
          <p class="text-xl font-bold text-green-700 dark:text-green-400 mt-1">
            {{ summaryStats().avgPresence }} {{ 'COMMON.DAY_SHORT' | translate }}
          </p>
        </div>
        <div
          class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-100 dark:border-blue-800"
        >
          <p class="text-xs text-blue-600 dark:text-blue-400">
            {{ 'ATTENDANCE.SUMMARY.AVG_HOURS' | translate }}
          </p>
          <p class="text-xl font-bold text-blue-700 dark:text-blue-400 mt-1">
            {{ summaryStats().avgHours }}{{ 'COMMON.HOUR_SHORT' | translate }}
          </p>
        </div>
        <div
          class="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800"
        >
          <p class="text-xs text-purple-600 dark:text-purple-400">
            {{ 'ATTENDANCE.SUMMARY.TOTAL_HOURS' | translate }}
          </p>
          <p class="text-xl font-bold text-purple-700 dark:text-purple-400 mt-1">
            {{ summaryStats().totalHours }}{{ 'COMMON.HOUR_SHORT' | translate }}
          </p>
        </div>
      </div>
    }

    <!-- Tabs + content -->
    <div
      class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
    >
      <!-- Tab bar -->
      <div class="flex border-b border-gray-100 dark:border-gray-700">
        <button
          (click)="switchManagerTab('today')"
          [class]="
            managerTab() === 'today'
              ? 'flex-1 py-3 text-xs sm:text-sm font-semibold text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
              : 'flex-1 py-3 text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          "
        >
          <lucide-icon [img]="Clock" class="inline mr-1" [size]="14" />
          {{ 'ATTENDANCE.TABS.TODAY' | translate }}
        </button>
        <button
          (click)="switchManagerTab('history')"
          [class]="
            managerTab() === 'history'
              ? 'flex-1 py-3 text-xs sm:text-sm font-semibold text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
              : 'flex-1 py-3 text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          "
        >
          <lucide-icon [img]="Calendar" class="inline mr-1" [size]="14" />
          {{ 'ATTENDANCE.TABS.HISTORY' | translate }}
        </button>
        <button
          (click)="switchManagerTab('summary')"
          [class]="
            managerTab() === 'summary'
              ? 'flex-1 py-3 text-xs sm:text-sm font-semibold text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
              : 'flex-1 py-3 text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          "
        >
          <lucide-icon [img]="BarChart2" class="inline mr-1" [size]="14" />
          {{ 'ATTENDANCE.TABS.SUMMARY' | translate }}
        </button>
      </div>

      <!-- Filters (today + history tabs) -->
      @if (managerTab() !== 'summary') {
        <div
          class="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-3 items-center"
        >
          @if (managerTab() === 'history') {
            <p-datepicker
              [(ngModel)]="selectedDate"
              (ngModelChange)="onFilterChange()"
              dateFormat="dd.mm.yy"
              [showClear]="false"
              appendTo="body"
              class="w-36"
            />
          }
          @if (storePick.visible()) {
            <p-select
              [(ngModel)]="selectedStoreId"
              (ngModelChange)="onFilterChange()"
              [options]="storeSelectOptions()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="'ATTENDANCE.FILTER.ALL_STORES' | translate"
              [showClear]="true"
              appendTo="body"
              class="w-44"
            />
          }
          <button
            (click)="onFilterChange()"
            class="ml-auto flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 hover:text-green-800 font-medium"
          >
            <lucide-icon [img]="RefreshCw" [size]="14" />
            {{ 'ATTENDANCE.FILTER.REFRESH' | translate }}
          </button>
        </div>
      }

      <!-- Summary filters -->
      @if (managerTab() === 'summary') {
        <div
          class="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-3 items-end"
        >
          <div class="flex flex-col gap-1">
            <span class="text-xs text-gray-500">{{ 'ATTENDANCE.FILTER.FROM' | translate }}</span>
            <p-datepicker
              [(ngModel)]="summaryFrom"
              dateFormat="dd.mm.yy"
              appendTo="body"
              class="w-36"
            />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-gray-500">{{ 'ATTENDANCE.FILTER.TO' | translate }}</span>
            <p-datepicker
              [(ngModel)]="summaryTo"
              dateFormat="dd.mm.yy"
              appendTo="body"
              class="w-36"
            />
          </div>
          @if (storePick.visible()) {
            <div class="flex flex-col gap-1">
              <span class="text-xs text-gray-500">{{ 'ATTENDANCE.FILTER.STORE' | translate }}</span>
              <p-select
                [(ngModel)]="summaryStoreId"
                [options]="storeSelectOptions()"
                optionLabel="label"
                optionValue="value"
                [placeholder]="'ATTENDANCE.FILTER.ALL_STORES' | translate"
                [showClear]="true"
                appendTo="body"
                class="w-44"
              />
            </div>
          }
          <button
            (click)="loadSummary()"
            class="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <lucide-icon [img]="RefreshCw" [size]="14" />
            {{ 'ATTENDANCE.FILTER.APPLY' | translate }}
          </button>
        </div>
      }

      <!-- ── TODAY TAB ── -->
      @if (managerTab() === 'today') {
        @if (todayLoading()) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="RefreshCw" class="animate-spin mx-auto mb-2" [size]="24" />
            {{ 'ATTENDANCE.LOADING' | translate }}
          </div>
        } @else if (todayList().length === 0) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="Users" class="mx-auto mb-2 text-gray-300" [size]="40" />
            <p class="text-sm">{{ 'ATTENDANCE.EMPTY_TODAY' | translate }}</p>
          </div>
        } @else {
          <div class="divide-y divide-gray-50 dark:divide-gray-700/50">
            @for (emp of todayList(); track emp.employeeId) {
              <div
                class="px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                  [class]="
                    emp.isPresent
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                  "
                >
                  {{ getInitials(emp.fullName) }}
                </div>
                <div class="flex-1 min-w-0">
                  <p class="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                    {{ emp.fullName }}
                  </p>
                  <p class="text-xs text-gray-400">{{ emp.phoneNumber }}</p>
                </div>
                <div class="text-right shrink-0">
                  @if (emp.isPresent) {
                    <div class="flex items-center gap-1 justify-end">
                      <div class="w-2 h-2 rounded-full bg-green-500"></div>
                      <span class="text-xs font-medium text-green-600 dark:text-green-400">{{
                        'ATTENDANCE.STATUS.PRESENT' | translate
                      }}</span>
                    </div>
                    <p class="text-xs text-gray-400 mt-0.5">
                      {{ formatTime(emp.checkInTime) }}
                      @if (emp.checkOutTime) {
                        → {{ formatTime(emp.checkOutTime) }}
                      }
                      @if (emp.storeName) {
                        · {{ emp.storeName }}
                      }
                    </p>
                  } @else {
                    <div class="flex items-center gap-1 justify-end">
                      <div class="w-2 h-2 rounded-full bg-gray-300"></div>
                      <span class="text-xs font-medium text-gray-400">{{
                        'ATTENDANCE.STATUS.ABSENT' | translate
                      }}</span>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        }
      }

      <!-- ── HISTORY TAB ── -->
      @if (managerTab() === 'history') {
        @if (historyLoading()) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="RefreshCw" class="animate-spin mx-auto mb-2" [size]="24" />
            {{ 'ATTENDANCE.LOADING' | translate }}
          </div>
        } @else if (historyList().length === 0) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="Calendar" class="mx-auto mb-2 text-gray-300" [size]="40" />
            <p class="text-sm">{{ 'ATTENDANCE.EMPTY_HISTORY' | translate }}</p>
          </div>
        } @else {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead
                class="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
              >
                <tr>
                  <th class="px-4 py-2.5 text-left">
                    {{ 'ATTENDANCE.TABLE.EMPLOYEE' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-left hidden sm:table-cell">
                    {{ 'ATTENDANCE.TABLE.STORE' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center">
                    {{ 'ATTENDANCE.TABLE.CHECK_IN' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center hidden sm:table-cell">
                    {{ 'ATTENDANCE.TABLE.CHECK_OUT' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center">
                    {{ 'ATTENDANCE.TABLE.DURATION' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center hidden sm:table-cell">
                    {{ 'ATTENDANCE.TABLE.STATUS' | translate }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50 dark:divide-gray-700/50">
                @for (rec of historyList(); track rec.id) {
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                    <td class="px-4 py-3">
                      <p class="font-medium text-gray-900 dark:text-gray-100">
                        {{ rec.employeeName }}
                      </p>
                      <p class="text-xs text-gray-400">{{ rec.employeePhone }}</p>
                    </td>
                    <td class="px-4 py-3 text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                      {{ rec.storeName }}
                    </td>
                    <td
                      class="px-4 py-3 text-center font-medium text-green-700 dark:text-green-400"
                    >
                      {{ formatTime(rec.checkInTime) }}
                    </td>
                    <td class="px-4 py-3 text-center text-gray-600 hidden sm:table-cell">
                      {{ formatTime(rec.checkOutTime) }}
                    </td>
                    <td class="px-4 py-3 text-center text-gray-500 text-xs">
                      {{ calcDuration(rec.checkInTime, rec.checkOutTime) }}
                    </td>
                    <td class="px-4 py-3 text-center hidden sm:table-cell">
                      @if (rec.checkOutTime) {
                        <span
                          class="inline-flex items-center gap-1 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full"
                        >
                          <lucide-icon [img]="CheckCircle" [size]="11" />{{
                            'ATTENDANCE.TABLE.COMPLETED' | translate
                          }}
                        </span>
                      } @else {
                        <span
                          class="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full"
                        >
                          <div class="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                          {{ 'ATTENDANCE.TABLE.WORKING' | translate }}
                        </span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (totalPages > 1) {
            <div
              class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500"
            >
              <span>{{ 'ATTENDANCE.TOTAL' | translate: { count: historyTotal() } }}</span>
              <div class="flex gap-1">
                @for (page of [].constructor(totalPages); track $index) {
                  <button
                    (click)="goPage($index + 1)"
                    [class]="
                      historyPage() === $index + 1
                        ? 'w-8 h-8 rounded-lg bg-green-600 text-white font-medium text-xs'
                        : 'w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs'
                    "
                  >
                    {{ $index + 1 }}
                  </button>
                }
              </div>
            </div>
          }
        }
      }

      <!-- ── SUMMARY TAB ── -->
      @if (managerTab() === 'summary') {
        @if (summaryLoading()) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="RefreshCw" class="animate-spin mx-auto mb-2" [size]="24" />
            {{ 'ATTENDANCE.LOADING' | translate }}
          </div>
        } @else if (summaryList().length === 0) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="BarChart2" class="mx-auto mb-2 text-gray-300" [size]="40" />
            <p class="text-sm">{{ 'ATTENDANCE.SUMMARY.EMPTY' | translate }}</p>
          </div>
        } @else {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead
                class="bg-gray-50 dark:bg-gray-700/50 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide"
              >
                <tr>
                  <th class="px-4 py-2.5 text-left">
                    {{ 'ATTENDANCE.TABLE.EMPLOYEE' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center">
                    {{ 'ATTENDANCE.SUMMARY.DAYS_PRESENT' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center">
                    {{ 'ATTENDANCE.SUMMARY.DAYS_ABSENT' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center hidden sm:table-cell">
                    {{ 'ATTENDANCE.SUMMARY.TOTAL_HOURS_COL' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center hidden sm:table-cell">
                    {{ 'ATTENDANCE.SUMMARY.AVG_HOURS_COL' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center hidden lg:table-cell">
                    {{ 'ATTENDANCE.SUMMARY.LATE' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-center hidden md:table-cell">
                    {{ 'ATTENDANCE.SUMMARY.ATTENDANCE_RATE' | translate }}
                  </th>
                  <th class="px-4 py-2.5 text-left hidden md:table-cell">
                    {{ 'ATTENDANCE.SUMMARY.LAST_SEEN' | translate }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50 dark:divide-gray-700/50">
                @for (emp of summaryList(); track emp.employeeId) {
                  <tr class="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-2">
                        <div
                          class="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold flex items-center justify-center shrink-0"
                        >
                          {{ getInitials(emp.fullName) }}
                        </div>
                        <div class="min-w-0">
                          <p class="font-medium text-gray-900 dark:text-gray-100 truncate">
                            {{ emp.fullName }}
                          </p>
                          @if (emp.scheduledCheckIn) {
                            <p class="text-xs text-gray-400">
                              {{ emp.scheduledCheckIn }} – {{ emp.scheduledCheckOut }}
                            </p>
                          } @else {
                            <p class="text-xs text-gray-400">{{ emp.phoneNumber }}</p>
                          }
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span
                        class="inline-block text-sm font-bold text-green-700 dark:text-green-400"
                        >{{ emp.daysPresent }}</span
                      >
                      <span class="text-xs text-gray-400"> / {{ emp.scheduledDays }}</span>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span
                        class="inline-block text-sm font-bold"
                        [class.text-red-600]="emp.daysAbsent > 0"
                        [class.text-gray-400]="emp.daysAbsent === 0"
                      >
                        {{ emp.daysAbsent }}
                      </span>
                    </td>
                    <td
                      class="px-4 py-3 text-center text-gray-700 dark:text-gray-300 font-medium hidden sm:table-cell"
                    >
                      {{ emp.totalHours }}{{ 'COMMON.HOUR_SHORT' | translate }}
                    </td>
                    <td class="px-4 py-3 text-center text-gray-500 hidden sm:table-cell">
                      {{ emp.avgHoursPerDay }}{{ 'COMMON.HOUR_SHORT' | translate }}/{{
                        'COMMON.DAY_SHORT' | translate
                      }}
                    </td>
                    <td class="px-4 py-3 text-center hidden lg:table-cell">
                      @if (emp.lateCount > 0) {
                        <span
                          class="inline-flex items-center gap-1 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full"
                        >
                          {{ emp.lateCount }}x
                        </span>
                      } @else {
                        <span class="text-xs text-gray-300">—</span>
                      }
                    </td>
                    <td class="px-4 py-3 hidden md:table-cell">
                      <div class="flex items-center gap-2">
                        <div class="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                          <div
                            class="bg-green-500 h-1.5 rounded-full"
                            [style.width.%]="presenceBar(emp.daysPresent, emp.scheduledDays)"
                          ></div>
                        </div>
                        <span class="text-xs text-gray-500 w-8 text-right"
                          >{{ presenceBar(emp.daysPresent, emp.scheduledDays) }}%</span
                        >
                      </div>
                    </td>
                    <td class="px-4 py-3 text-xs text-gray-400 hidden md:table-cell">
                      {{ emp.lastSeen ?? '—' }}
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
    </div>
  }

  <!-- ── EMPLOYEE VIEW ──────────────────────────────────────────────────────── -->
  @if (!isManagerView()) {
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
        {{ 'ATTENDANCE.MY_TITLE' | translate }}
      </h1>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
        {{ 'ATTENDANCE.MY_SUBTITLE' | translate }}
      </p>
    </div>

    <!-- Tabs -->
    <div
      class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
    >
      <div class="flex border-b border-gray-100 dark:border-gray-700">
        <button
          (click)="switchEmployeeTab('my-today')"
          [class]="
            employeeTab() === 'my-today'
              ? 'flex-1 py-3 text-sm font-semibold text-green-600 dark:text-green-400 border-b-2 border-green-600 bg-green-50 dark:bg-green-900/20'
              : 'flex-1 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700'
          "
        >
          <lucide-icon [img]="Clock" class="inline mr-1.5" [size]="15" />
          {{ 'ATTENDANCE.TABS.TODAY' | translate }}
        </button>
        <button
          (click)="switchEmployeeTab('my-history')"
          [class]="
            employeeTab() === 'my-history'
              ? 'flex-1 py-3 text-sm font-semibold text-green-600 dark:text-green-400 border-b-2 border-green-600 bg-green-50 dark:bg-green-900/20'
              : 'flex-1 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700'
          "
        >
          <lucide-icon [img]="Calendar" class="inline mr-1.5" [size]="15" />
          {{ 'ATTENDANCE.TABS.MY_HISTORY' | translate }}
        </button>
      </div>

      <!-- ── MY TODAY ── -->
      @if (employeeTab() === 'my-today') {
        @if (myTodayLoading()) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="RefreshCw" class="animate-spin mx-auto mb-2" [size]="24" />
            {{ 'ATTENDANCE.LOADING' | translate }}
          </div>
        } @else {
          <div class="p-6 space-y-5">
            <!-- Status card -->
            <div
              class="rounded-xl p-5 border-2 text-center"
              [class]="
                myToday()
                  ? 'border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800'
                  : 'border-gray-200 bg-gray-50 dark:bg-gray-700/30 dark:border-gray-600'
              "
            >
              <div
                class="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-3"
                [class]="
                  myToday() ? 'bg-green-100 dark:bg-green-900/40' : 'bg-gray-100 dark:bg-gray-700'
                "
              >
                <lucide-icon
                  [img]="myToday() ? UserCheck : UserX"
                  [class]="myToday() ? 'text-green-600' : 'text-gray-400'"
                  [size]="28"
                />
              </div>
              @if (myToday(); as rec) {
                <p class="text-lg font-bold text-green-700 dark:text-green-400">
                  {{ 'ATTENDANCE.STATUS.PRESENT' | translate }}
                </p>
                <p class="text-sm text-gray-500 mt-1">
                  {{ 'ATTENDANCE.MY_CHECKIN' | translate }}:
                  <span class="font-semibold text-gray-700 dark:text-gray-300">{{
                    formatTime(rec.checkInTime)
                  }}</span>
                  @if (rec.checkOutTime) {
                    · {{ 'ATTENDANCE.TABLE.CHECK_OUT' | translate }}:
                    <span class="font-semibold">{{ formatTime(rec.checkOutTime) }}</span> ·
                    {{ calcDuration(rec.checkInTime, rec.checkOutTime) }}
                  } @else {
                    ·
                    <span class="text-amber-600 font-medium">{{
                      'ATTENDANCE.TABLE.WORKING' | translate
                    }}</span>
                  }
                </p>
                @if (rec.storeName) {
                  <p class="text-xs text-gray-400 mt-1">{{ rec.storeName }}</p>
                }
              } @else {
                <p class="text-lg font-bold text-gray-500 dark:text-gray-400">
                  {{ 'ATTENDANCE.STATUS.ABSENT' | translate }}
                </p>
                <p class="text-sm text-gray-400 mt-1">
                  {{ 'ATTENDANCE.MY_NOT_CLOCKED' | translate }}
                </p>
              }
            </div>

            <!-- Action buttons -->
            @if (canClock()) {
              <div class="flex gap-3">
                @if (!myToday()) {
                  <button
                    (click)="checkIn()"
                    [disabled]="clockLoading()"
                    class="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
                  >
                    <lucide-icon [img]="LogIn" [size]="18" />
                    {{
                      clockLoading()
                        ? ('ATTENDANCE.MY_LOCATING' | translate)
                        : ('ATTENDANCE.MY_CHECKIN_BTN' | translate)
                    }}
                  </button>
                } @else if (!myToday()?.checkOutTime) {
                  <button
                    (click)="checkOut()"
                    [disabled]="clockLoading()"
                    class="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
                  >
                    <lucide-icon [img]="LogOut" [size]="18" />
                    {{
                      clockLoading()
                        ? ('ATTENDANCE.MY_LOCATING' | translate)
                        : ('ATTENDANCE.MY_CHECKOUT_BTN' | translate)
                    }}
                  </button>
                } @else {
                  <div
                    class="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-medium py-3 rounded-xl"
                  >
                    <lucide-icon [img]="CheckCircle" [size]="18" />
                    {{ 'ATTENDANCE.MY_DONE' | translate }}
                  </div>
                }
                <button
                  (click)="loadMyToday()"
                  [disabled]="myTodayLoading()"
                  class="w-12 flex items-center justify-center border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-400 transition-colors"
                >
                  <lucide-icon
                    [img]="RefreshCw"
                    [size]="16"
                    [class]="myTodayLoading() ? 'animate-spin' : ''"
                  />
                </button>
              </div>
            }
          </div>
        }
      }

      <!-- ── MY HISTORY ── -->
      @if (employeeTab() === 'my-history') {
        <!-- My period stats -->
        @if (myHistoryList().length > 0) {
          <div class="p-4 border-b border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-3">
            <div class="text-center">
              <p class="text-xs text-gray-400">
                {{ 'ATTENDANCE.SUMMARY.DAYS_PRESENT' | translate }}
              </p>
              <p class="text-lg font-bold text-green-600">{{ myStats().days }}</p>
            </div>
            <div class="text-center">
              <p class="text-xs text-gray-400">
                {{ 'ATTENDANCE.SUMMARY.TOTAL_HOURS_COL' | translate }}
              </p>
              <p class="text-lg font-bold text-blue-600">
                {{ myStats().totalHours }}{{ 'COMMON.HOUR_SHORT' | translate }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-xs text-gray-400">
                {{ 'ATTENDANCE.SUMMARY.AVG_HOURS_COL' | translate }}
              </p>
              <p class="text-lg font-bold text-purple-600">
                {{ myStats().avgHours }}{{ 'COMMON.HOUR_SHORT' | translate }}
              </p>
            </div>
          </div>
        }

        <!-- Filters -->
        <div
          class="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-3 items-end"
        >
          <div class="flex flex-col gap-1">
            <span class="text-xs text-gray-500">{{ 'ATTENDANCE.FILTER.FROM' | translate }}</span>
            <p-datepicker
              [(ngModel)]="myHistoryFrom"
              dateFormat="dd.mm.yy"
              appendTo="body"
              class="w-36"
            />
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs text-gray-500">{{ 'ATTENDANCE.FILTER.TO' | translate }}</span>
            <p-datepicker
              [(ngModel)]="myHistoryTo"
              dateFormat="dd.mm.yy"
              appendTo="body"
              class="w-36"
            />
          </div>
          <button
            (click)="loadMyHistory()"
            class="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            <lucide-icon [img]="RefreshCw" [size]="14" />
            {{ 'ATTENDANCE.FILTER.APPLY' | translate }}
          </button>
        </div>

        @if (myHistoryLoading()) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="RefreshCw" class="animate-spin mx-auto mb-2" [size]="24" />
            {{ 'ATTENDANCE.LOADING' | translate }}
          </div>
        } @else if (myHistoryList().length === 0) {
          <div class="p-12 text-center text-gray-400">
            <lucide-icon [img]="Calendar" class="mx-auto mb-2 text-gray-300" [size]="40" />
            <p class="text-sm">{{ 'ATTENDANCE.EMPTY_HISTORY' | translate }}</p>
          </div>
        } @else {
          <div class="divide-y divide-gray-50 dark:divide-gray-700/50">
            @for (rec of myHistoryList(); track rec.id) {
              <div class="px-4 py-3 flex items-center gap-3">
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {{ rec.workDate }}
                  </p>
                  <p class="text-xs text-gray-400">{{ rec.storeName }}</p>
                </div>
                <div class="text-right shrink-0">
                  <p class="text-sm font-medium text-green-700 dark:text-green-400">
                    {{ formatTime(rec.checkInTime) }}
                    @if (rec.checkOutTime) {
                      → {{ formatTime(rec.checkOutTime) }}
                    }
                  </p>
                  <p class="text-xs text-gray-400">
                    {{ calcDuration(rec.checkInTime, rec.checkOutTime) }}
                  </p>
                </div>
                <div class="shrink-0 ml-1">
                  @if (rec.checkOutTime) {
                    <lucide-icon [img]="CheckCircle" class="text-green-500" [size]="16" />
                  } @else {
                    <div class="w-3 h-3 rounded-full bg-amber-400"></div>
                  }
                </div>
              </div>
            }
          </div>
          @if (myTotalPages > 1) {
            <div
              class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-1"
            >
              @for (p of [].constructor(myTotalPages); track $index) {
                <button
                  (click)="myHistoryPage.set($index + 1); loadMyHistory()"
                  [class]="
                    myHistoryPage() === $index + 1
                      ? 'w-8 h-8 rounded-lg bg-green-600 text-white text-xs font-medium'
                      : 'w-8 h-8 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 text-xs'
                  "
                >
                  {{ $index + 1 }}
                </button>
              }
            </div>
          }
        }
      }
    </div>
  }
</div>
